/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./VccDetailReportValidate";
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

import useOrgRoleData from '@usas/ui-web/state/orgRole/UseOrgRoleData';

import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "400px", id: "SeasonName", field: "seasonName", title: "Season Name" },
  { width: "100px", id: "FirstName", field: "firstName", title: "First Name" },
  { width: "100px", id: "MiddleName", field: "middleName", title: "Middle Name" },
  { width: "120px", id: "LastName", field: "lastName", title: "Last Name" },
  { width: "150px", id: "MemberId", field: "memberId", title: "Member ID" },
  { width: "160px", id: "EventGender", field: "eventGender", title: "Competition Category" },
  { width: "90px", id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { width: "90px", id: "LscCode", field: "lscCode", title: "LSC" },
  { width: "100px", id: "ClubCode", field: "clubCode", title: "Club Code" },
  { width: "310px", id: "ClubName", field: "clubName", title: "Club Name" },
  { width: "100px", id: "TotalPoints", field: "totalPoints", title: "Total Points" },
  { width: "150px", id: "PercentTotalPoints", field: "percentTotalPoints", title: "Percent Total Points", format: "{0:p2}" }
];

const excelColumns = [
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "SeasonName", field: "seasonName", title: "Season Name" },
  { id: "FirstName", field: "firstName", title: "First Name" },
  { id: "MiddleName", field: "middleName", title: "Middle Name" },
  { id: "LastName", field: "lastName", title: "Last Name" },
  { id: "MemberId", field: "memberId", title: "Member ID" },
  { id: "EventGender", field: "eventGender", title: "Competition Category" },  
  { id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { id: "LscCode", field: "lscCode", title: "LSC" },
  { id: "ClubCode", field: "clubCode", title: "Club Code" },
  { id: "ClubName", field: "clubName", title: "Club Name" },
  { id: "TotalPoints", field: "totalPoints", title: "Total Points" },
  { id: "PercentTotalPoints", field: "percentTotalPoints", title: "Percent Total Points" },
  { id: "VccDetail_EventCode", field: "vccDetail_eventCode", title: "Event" },
  { id: "VccDetail_SwimTime", field: "vccDetail_swimTime", title: "Swim Time" },
  { id: "VccDetail_SwimDate", field: "vccDetail_swimDate", title: "Swim Date" },
  { id: "VccDetail_MeetName", field: "vccDetail_meetName", title: "Meet Name" },
  { id: "VccDetail_VccPoints", field: "vccDetail_points", title: "Points" },
  { id: "VccDetail_PersonPercentTotalPoints", field: "vccDetail_percentTotalPoints", title: "Percent Total Points" }
];

const REPORT_INFO_ID = 289;

const useVccDetailReport = () => {
  const [ modalState, setModalState ] = useState(getInitialModalState());

  const { orgUnitState } = useOrgUnitData();
  const { orgRoleState } = useOrgRoleData();

  const {
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty,
    updateFormState
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      minAge: 1,
      maxAge: 99,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      clubs: [],
      lscs: [],
      zones: [],
      vccSeasonId: 43,
      exportDetailData: false
    };
  }

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      Genders: formState.genders.map(gender => gender.id).join(','), 
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','),
      VccSeason_Int: formState.vccSeasonId
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    orgRoleState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID: REPORT_INFO_ID
  };
};

export default useVccDetailReport;